/**
 * Insight copy builders for the love-receive comparison screen.
 *
 * The backend returns raw scores + an optional legacy insights payload; the
 * current Comparison UI derives its own two insight cards by reading each
 * partner's ranked language list directly. These functions are pure — given
 * the same input they always return the same copy — so they're kept here
 * (isolated from the rendering layer) and tested independently of React.
 *
 * Mirrors the structure of `api/src/assessment/love_receive/insights.py`
 * on the backend.
 */

import type { LanguageCopy, LanguageKey } from "./api/types";
import { rankedByScore } from "./utils/ranking";

export type InsightCardContent = {
  title: string;
  body: string;
  /** Optional "Try this" action appended beneath the body in a divided block. */
  tryThis?: string;
};

/**
 * Card 1 — "How you share love".
 *
 * Names the viewer's top language + the concrete behavior, then frames how
 * well that lands for their partner based on the partner's rank of the same
 * language. Three tonal variants based on partner's rank.
 */
export function howYouShareInsight(args: {
  yourTopCopy: Pick<LanguageCopy, "display_name" | "behavior_short">;
  partnerName: string;
  partnerRankOfYourTop: number;
}): InsightCardContent {
  const { yourTopCopy, partnerName, partnerRankOfYourTop } = args;
  const lead = `You rank ${yourTopCopy.display_name} at #1. When you express love, you ${yourTopCopy.behavior_short}.`;
  const title = "How you share love";

  if (partnerRankOfYourTop <= 2) {
    return {
      title,
      body: `${lead} ${partnerName} also ranks ${yourTopCopy.display_name} in their top 2, so your natural instinct already lands for them.`,
    };
  }
  if (partnerRankOfYourTop === 3) {
    return {
      title,
      body: `${lead} For ${partnerName}, ${yourTopCopy.display_name} sits in the middle. It lands, but not at full volume.`,
    };
  }
  return {
    title,
    body: `${lead} But ${partnerName} rates ${yourTopCopy.display_name} at #${partnerRankOfYourTop}, so your effort gets received more quietly than you're sending it.`,
  };
}

/**
 * Card 2 — "What {partner} would prefer".
 *
 * Names the partner's top language with a concrete behavior + weekly action
 * (interpolated from the language's `try_this` template). If the partner's
 * top matches the viewer's top, the framing celebrates alignment rather
 * than contrasting the two.
 */
export function whatPartnerPrefersInsight(args: {
  partnerTopCopy: Pick<LanguageCopy, "display_name" | "behavior_short" | "try_this">;
  partnerName: string;
  isAlignedWithYours: boolean;
}): InsightCardContent {
  const { partnerTopCopy, partnerName, isAlignedWithYours } = args;
  const tryThis = partnerTopCopy.try_this.replace(/\{partner\}/g, partnerName);
  const title = `What ${partnerName} would prefer`;

  if (isAlignedWithYours) {
    return {
      title,
      body: `${partnerName}'s top is the same as yours: ${partnerTopCopy.display_name}. Your instinct and their need are the same language. Keep doing what comes naturally.`,
      tryThis,
    };
  }
  return {
    title,
    body: `${partnerName}'s top language is ${partnerTopCopy.display_name}. When you ${partnerTopCopy.behavior_short}, that's love at full volume for ${partnerName}.`,
    tryThis,
  };
}

/**
 * Convenience builder — derives both cards from raw score maps so the
 * rendering component doesn't have to know about ranking or alignment.
 */
export function buildComparisonInsights(args: {
  languageOrder: LanguageKey[];
  languages: Record<LanguageKey, LanguageCopy>;
  youScores: Record<LanguageKey, number>;
  partnerScores: Record<LanguageKey, number>;
  partnerName: string;
}): { card1: InsightCardContent; card2: InsightCardContent } {
  const { languageOrder, languages, youScores, partnerScores, partnerName } = args;

  const yourRanked = rankedByScore(languageOrder, youScores);
  const partnerRanked = rankedByScore(languageOrder, partnerScores);
  const yourTopKey = yourRanked[0];
  const partnerTopKey = partnerRanked[0];

  return {
    card1: howYouShareInsight({
      yourTopCopy: languages[yourTopKey],
      partnerName,
      partnerRankOfYourTop: partnerRanked.indexOf(yourTopKey) + 1,
    }),
    card2: whatPartnerPrefersInsight({
      partnerTopCopy: languages[partnerTopKey],
      partnerName,
      isAlignedWithYours: yourTopKey === partnerTopKey,
    }),
  };
}
